import { MultiPoint, type PointCapture } from './multipoint';
import { nelderMead } from './nelderMead';
import { Vector } from './vector';

export type { NelderMeadResult } from './nelderMead';

export interface CatSagResult {
	maxSag: number;
	maxX: number;
	maxY: number;
	maxSlopeY: number;
}

/** Defines a catenary sag curve of a wire given the start point, end point, and a single mid point along the wire. */
export class CatSagOptimizer {
	/** Starting point of the wire, should be 0,startY (m) */
	start: Vector;
	/** Ending point of the wire (m) */
	end: Vector;
	/** Mid point of the wire (m) */
	mid: Vector;

	/** The slope of the straight line (rise over run) between start and end points */
	lineSlope: number;

	/** a is the standard mathematical name for the scaling factor which describes how much curve is in the wire
	 * - Ref https://en.wikipedia.org/wiki/Catenary */
	a: number;
	/** x offset that will be found by the solver required to position the lowest point of the cosh function at exactly 0,0 */
	xOffset: number;
	/** y offset that will be found by the solver required to position the lowest point of the cosh function at exactly 0,0 */
	yOffset: number;

	/**
	 * 2D Cartesian input points start, end, and mid that lie on the vertical plane containing the wire
	 * IKE Field calls these P1, P2, and P3 respectively
	 *
	 * @param start start which is always 0,0 - Pole wire start point - will normally be an insulator
	 * @param end Pole wire end point - will also normally be an insulator
	 * @param mid Targeted point on the wire - Not necessarily at peak sag location
	 */
	constructor(start: Vector, end: Vector, mid: Vector) {
		this.start = start;
		this.end = end;
		this.mid = mid;
		this.lineSlope = (this.end.y - this.start.y) / (this.end.x - this.start.x);

		this.optimizeCurve();
	}

	/** Create a sag optimizer from a collection of captures */
	static fromCaptures(captureStart: PointCapture, captureEnd: PointCapture, captureMid: PointCapture): CatSagOptimizer {
		const sagPoints = MultiPoint.getSagPoints2D(captureStart, captureEnd, captureMid);
		return new CatSagOptimizer(sagPoints.start, sagPoints.end, sagPoints.mid);
	}

	/** Optimize the curve using Nelder-Mead minimization to find the best fitting sag line. */
	private optimizeCurve(): void {
		if (this.isOutOfBounds()) {
			return;
		}

		// Ensure `mid` is below slope defined by `start` and `end`
		const sign = this.end.cross(this.mid).z;
		if (sign > 0) {
			// It is possible that a mid point measurement would be above the line from start to end
			// (e.g. if line is very taut, and measurement accuracy puts point slightly high.)
			// To deal with this, calculate a point on the straight line between start and end directly below
			// the midpoint
			this.mid.y = this.mid.x * this.lineSlope;
		}

		// Start with a default curve estimate - Larger values of 'a' lead to a flatter curve / taut cable
		// See Wikipedia: https://en.wikipedia.org/wiki/Catenary
		const aInitial = 1.0;
		const xOffsetInitial = -(this.end.x - this.start.x) / 2.0;
		const yOffsetInitial = this.start.y - this.mid.y;

		const result = nelderMead(this.minimizeFuncNelder.bind(this), [aInitial, xOffsetInitial, yOffsetInitial]);
		if (result.fx > 0.001) {
			return; // Failed to converge
		}

		this.a = result.x[0];
		this.xOffset = result.x[1];
		this.yOffset = result.x[2];
	}

	/**
	 * Check the input points are valid
	 * Because of the way coordinates are generated from the 3d points in world-space
	 * the end point should always be further from the origin than the start point(+ direction)
	 *
	 * The mid-point needs to be between the start and end points, along the x axis
	 *
	 * @return {boolean} true if the given coordinates are invalid
	 * */
	private isOutOfBounds(): boolean {
		if (this.start.x > this.end.x) {
			return true;
		}

		if (this.mid.x < this.start.x || this.mid.x > this.end.x) {
			return true;
		}

		return false;
	}

	/**
	 * Calculate the full length of the wire including sag.
	 * Denoted as s in the Wikipedia article:
	 * https://en.wikipedia.org/wiki/Catenary#Determining_parameters
	 *
	 * @returns Wire length (m), or null if out of bounds
	 */
	getLength(): number | null {
		if (this.isOutOfBounds()) {
			return null;
		}
		// returns negative value if the end is end has a smaller x value than the start.
		const s =
			this.a * Math.sinh((this.end.x + this.xOffset) / this.a) -
			this.a * Math.sinh((this.start.x + this.xOffset) / this.a);

		// Length is always positive
		return Math.abs(s);
	}

	/**
	 * Find the maximal sag.
	 * @returns Sag result, or null if out of bounds or result is invalid
	 */
	getSag(): CatSagResult | null {
		if (this.isOutOfBounds()) {
			return null;
		}

		// Maximize the sag value using Nelder Mead, starting from the center of the wire.
		const midX = (this.start.x + this.end.x) / 2;
		const result = nelderMead(this.maximizeFuncNelder.bind(this), [midX]);
		const sagX = result.x[0]!;

		if (sagX < this.start.x || sagX > this.end.x) {
			return null;
		}

		return this.getSagResult(sagX);
	}

	/**
	 * Get ths sag result given a specific X
	 * @param x X offset to calculate for
	 */
	public getSagResult(x: number): CatSagResult {
		const slopeY = x * this.lineSlope;
		const catY = this.getCurveHeight(this.a, x + this.xOffset) - this.yOffset;

		return {
			maxSag: slopeY - catY,
			maxX: x,
			maxY: catY,
			maxSlopeY: slopeY,
		};
	}

	/**
	 * Maximize the sag height
	 *
	 * To maximize the sag, we invert the sag result as the nelder mead expects to minimize the sag.
	 *
	 * @param args xOffset to calculate the sag
	 **/
	private maximizeFuncNelder(args: number[]) {
		const x = args[0]!;

		// Out of bounds no correct answer
		if (x < this.start.x || x > this.end.x) {
			return 0;
		}

		const slopeY = x * this.lineSlope;
		const catY = this.getCurveHeight(this.a, x + this.xOffset) - this.yOffset;
		const sag = slopeY - catY;

		// NelderMead is looking for a minimum so convert this negative
		// so that it will converge on the correct point
		return -1 * sag;
	}

	/**
	 * Minimization function.
	 *
	 * This function is called repeatedly until the minimal distance to mid is found.
	 **/
	private minimizeFuncNelder(args: number[]): number {
		return this.calcCurveError(args[0]!, args[1]!, args[2]!);
	}

	/**
	 * Find the height of the sag at position x offset from start.x
	 * @param a Curve factor for the cosh function - Indicates how much sag the wire has. Larger a => flatter curve / more taut wire
	 * @param x X position to find the height for
	 * @returns Height of sag in m
	 */
	public getCurveHeight(a: number, x: number): number {
		return a * Math.cosh(x / a) - a;
	}

	/**
	 * Calculate the overall RMS error between the start, mid, and end points and the resulting catenary curve
	 * when using the given curve factor (a), and 2D offset (xOffset, yOffset)
	 * @param a
	 * @param xOffset
	 * @param yOffset
	 */
	private calcCurveError(a: number, xOffset: number, yOffset: number) {
		const MAX_ERROR = 100000000;
		if (a <= 0.0 || xOffset > 0) {
			return MAX_ERROR;
		}

		const offset = new Vector(xOffset, yOffset);

		const transformedStart = this.start.add(offset);
		const transformedMid = this.mid.add(offset);
		const transformedEnd = this.end.add(offset);

		const calculatedStartY = this.getCurveHeight(a, transformedStart.x);
		const calculatedMidY = this.getCurveHeight(a, transformedMid.x);
		const calculatedEndY = this.getCurveHeight(a, transformedEnd.x);

		const rmsError = Math.sqrt(
			Math.pow(calculatedStartY - transformedStart.y, 2) +
				Math.pow(calculatedMidY - transformedMid.y, 2) +
				Math.pow(calculatedEndY - transformedEnd.y, 2)
		);

		return rmsError;
	}
}
